import { ContentPlayback } from '../model/ContentPlayback';
import { useContentCard } from '../model/useContentCard';
import NewIcon from '@/assets/icons/new.png';
import PremiumIcon from '@/assets/icons/premium-locked.png';
import { Expose } from '@/shared/ui';
import { Content } from '@/types';
import { formatSecondsToHMS } from '@/utils';
import isNil from 'lodash/isNil';
import { useMemo } from 'react';

export type ContentCardProps = {
  sourcePage: string;
  content: Content;
  tabIndex: number;
  focusKey?: string;
  onFocused?: ({ x, y, left, top }: { x: number; y: number; left: number; top: number }) => void;
  rank?: number;
  variant?: 'default' | 'rank';
  onExposed?: () => void;
  weight: number;
  containerId?: number;
  topicId?: number;
};

export const ContentCard = ({
  sourcePage,
  content,
  tabIndex,
  focusKey,
  onFocused,
  rank,
  variant,
  onExposed,
  weight,
  containerId,
  topicId,
}: ContentCardProps) => {
  const { ref, progress, handleClick, handleExposed } = useContentCard({
    content,
    sourcePage,
    weight,
    focusKey,
    containerId,
    topicId,
    onFocused,
    onExposed,
  });

  const className = useMemo(() => {
    if (variant === 'rank') {
      return 'w-[373px] h-[210px] mr-[48px] mb-[14px] relative ease-in-out duration-300 rounded-[20px] outline-disable outline-primary-12 bg-gray-400';
    } else if (variant === 'default') {
      return 'w-[480px] h-[270px] mr-[18px] mb-[20px] relative overflow-hidden ease-in-out duration-300 rounded-[20px] outline-disable outline-primary-12 bg-gray-400';
    } else {
      return 'w-[533px] h-[300px] mr-[20px] mb-[20px] relative overflow-hidden ease-in-out duration-300 rounded-[20px] outline-disable outline-primary-12 bg-gray-400';
    }
  }, [variant]);

  const cardSize = useMemo(() => {
    if (variant === 'rank') {
      return { width: 373, height: 210 };
    } else if (variant === 'default') {
      return { width: 480, height: 270 };
    } else {
      return { width: 533, height: 300 };
    }
  }, [variant]);

  return (
    <Expose
      targetRef={ref}
      threshold={Number.EPSILON}
      tabIndex={tabIndex}
      className={className}
      onClick={handleClick}
      onContextMenu={handleClick}
      onExposed={handleExposed}
      optimize
    >
      {/* 썸네일 */}
      <img
        src={content.thumbnail}
        className="w-full h-full absolute object-cover rounded-[20px]"
        loading="lazy"
        width={cardSize.width}
        height={cardSize.height}
      />

      {/* 유료 콘텐츠 스티커 */}
      {ContentPlayback.isLocked(content) && (
        <img src={PremiumIcon} className="absolute top-[8px] right-[8px]" width={58} height={58} />
      )}

      {/* 신규 콘텐츠 스티커 */}
      {content.sticker === 'NEW' && (
        <img src={NewIcon} className="absolute top-[10px] left-[10px] w-[73px] h-[34px]" width={73} height={34} />
      )}

      {/* 영상 재생 시간 */}
      {!isNil(content.duration) && (
        <span className="absolute bg-[rgba(0,0,0,0.3)] bottom-[50px] right-[8px] rounded-[5px] text-[16px] px-[10px] py-[2px] text-white">
          {formatSecondsToHMS(content.duration)}
        </span>
      )}

      {/* 영상 타이틀 */}
      <span className="absolute w-full bg-[rgba(0,0,0,0.3)] rounded-b-[20px] text-white left-0 bottom-0 px-[16px] py-[2px] tracking-wide whitespace-nowrap text-[26px] overflow-hidden text-ellipsis block">
        {content.title}
      </span>

      {/* 시청 이력 */}
      {!isNil(progress) && (
        <div className="absolute bottom-[43px] left-0 h-[4px] bg-secondary" style={{ width: `${progress}%` }} />
      )}

      {/* 랭킹 */}
      {rank && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 text-[160px] text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
          {rank}
        </span>
      )}
    </Expose>
  );
};
